/**
 * Design Tokens - Enterprise ERP Design System
 * Industry-standard design tokens for professional, scalable ERP applications
 * Inspired by SAP Fiori, Oracle Fusion, Microsoft Fluent, and Material Design 3
 */

export const designTokens = {
  // ============================================
  // COLOR PALETTE - Professional ERP Colors
  // ============================================
  colors: {
    // Primary - Green (Professional ERP Brand)
    primary: {
      50: '#E8F9EF', // Lightest green
      100: '#C6F1D9',
      200: '#9DE7BE',
      300: '#6DDB9D',
      400: '#3FD07A',
      500: '#1ABC55', // Main - Brand Green
      600: '#15A349', // Darker green
      700: '#118A3E', // Deep green
      800: '#0D7133',
      900: '#095828', // Darkest green
    },

    // Secondary - Teal (Complementary Accent)
    secondary: {
      50: '#f0fdfa', // Lightest teal
      100: '#ccfbf1',
      200: '#99f6e4',
      300: '#5eead4',
      400: '#2dd4bf',
      500: '#14b8a6', // Main - Teal 500
      600: '#0d9488', // Darker teal
      700: '#0f766e', // Deep teal
      800: '#115e59',
      900: '#134e4a', // Darkest teal
    },

    // Tertiary - Dark Navy (Professional Dark Backgrounds)
    tertiary: {
      50: '#1e3a5f',
      100: '#1a3352',
      200: '#172d47',
      300: '#14273d',
      400: '#112133',
      500: '#0f1e30', // Main dark background
      600: '#0d1926',
      700: '#0b151f',
      800: '#0a1628', // Deepest dark background
      900: '#050c14',
    },

    // Neutral - Grays for UI Elements
    neutral: {
      0: '#FFFFFF',
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      1000: '#000000',
    },

    // Semantic Colors - Status & Feedback
    success: {
      50: '#E8F5E9',
      100: '#C8E6C9',
      500: '#4CAF50', // Main
      700: '#388E3C',
      900: '#1B5E20',
    },

    warning: {
      50: '#FFF3E0',
      100: '#FFE0B2',
      500: '#FF9800', // Main
      700: '#F57C00',
      900: '#E65100',
    },

    error: {
      50: '#FFEBEE',
      100: '#FFCDD2',
      500: '#F44336', // Main
      700: '#D32F2F',
      900: '#B71C1C',
    },

    info: {
      50: '#E3F2FD',
      100: '#BBDEFB',
      500: '#2196F3', // Main
      700: '#1976D2',
      900: '#0D47A1',
    },

    // Data Visualization - Charts & Graphs
    dataViz: {
      blue: '#2196F3',
      green: '#4CAF50',
      orange: '#FF9800',
      red: '#F44336',
      purple: '#9C27B0',
      teal: '#009688',
      indigo: '#3F51B5',
      pink: '#E91E63',
      amber: '#FFC107',
      cyan: '#00BCD4',
      lime: '#CDDC39',
      deepOrange: '#FF5722',
    },

    // Background Colors
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
      elevated: '#FFFFFF',
      // Dark Mode Backgrounds
      dark: '#0a1628', // Deep dark navy
      darkPaper: '#0f1f3a', // Slightly lighter dark navy
      darkElevated: '#1a2942', // Elevated dark surfaces
    },

    // Text Colors
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.60)',
      disabled: 'rgba(0, 0, 0, 0.38)',
      hint: 'rgba(0, 0, 0, 0.38)',
      primaryDark: 'rgba(255, 255, 255, 0.87)',
      secondaryDark: 'rgba(255, 255, 255, 0.60)',
      disabledDark: 'rgba(255, 255, 255, 0.38)',
    },

    // Border Colors
    border: {
      light: '#E0E0E0',
      medium: '#BDBDBD',
      dark: '#757575',
    },

    // Overlay Colors
    overlay: {
      light: 'rgba(0, 0, 0, 0.04)',
      medium: 'rgba(0, 0, 0, 0.08)',
      dark: 'rgba(0, 0, 0, 0.12)',
      darker: 'rgba(0, 0, 0, 0.24)',
    },
  },

  // ============================================
  // TYPOGRAPHY - Professional Font System
  // ============================================
  typography: {
    fontFamily: {
      primary: '"Poppins", "Helvetica Neue", Arial, sans-serif',
      secondary: '"Poppins", "Helvetica Neue", Arial, sans-serif',
      monospace: '"Roboto Mono", "Courier New", monospace',
    },

    fontSize: {
      '2xs': '0.6875rem', // 11px — compact labels
      xs: '0.75rem', // 12px
      ui: '0.8125rem', // 13px — compact UI text
      sm: '0.875rem', // 14px
      base: '1rem', // 16px
      lg: '1.125rem', // 18px
      xl: '1.25rem', // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
      '5xl': '3rem', // 48px
      '6xl': '3.75rem', // 60px
    },

    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },

    lineHeight: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.75,
      loose: 2,
    },

    letterSpacing: {
      tight: '-0.02em',
      normal: '0',
      wide: '0.02em',
      wider: '0.05em',
    },
  },

  // ============================================
  // SPACING - 8px Grid System
  // ============================================
  spacing: {
    0: '0px',
    1: '0.25rem', // 4px
    2: '0.5rem', // 8px
    3: '0.75rem', // 12px
    4: '1rem', // 16px
    5: '1.25rem', // 20px
    6: '1.5rem', // 24px
    8: '2rem', // 32px
    10: '2.5rem', // 40px
    12: '3rem', // 48px
    16: '4rem', // 64px
    20: '5rem', // 80px
    24: '6rem', // 96px
  },

  // ============================================
  // SHADOWS - Elevation System
  // ============================================
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',

    // Material Design Elevation
    elevation: {
      0: 'none',
      1: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
      2: '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
      3: '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
      4: '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
      6: '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
      8: '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
      12: '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
      16: '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
      24: '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
    },
  },

  // ============================================
  // BORDER RADIUS - Rounded Corners
  // ============================================
  borderRadius: {
    none: '0',
    sm: '0.125rem', // 2px
    base: '0.25rem', // 4px
    md: '0.375rem', // 6px
    lg: '0.5rem', // 8px
    xl: '0.75rem', // 12px
    '2xl': '1rem', // 16px
    '3xl': '1.5rem', // 24px
    full: '9999px',
  },

  // ============================================
  // Z-INDEX - Stacking Order
  // ============================================
  zIndex: {
    hide: -1,
    base: 0,
    dropdown: 1000,
    sticky: 1020,
    overlay: 1030,
    modal: 1040,
    popover: 1050,
    tooltip: 1060,
    notification: 1070,
    max: 9999,
  },

  // ============================================
  // TRANSITIONS - Animation Timing
  // ============================================
  transitions: {
    duration: {
      fastest: '100ms',
      fast: '200ms',
      normal: '300ms',
      slow: '400ms',
      slowest: '500ms',
    },

    timing: {
      linear: 'linear',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },

  // ============================================
  // BREAKPOINTS - Responsive Design
  // ============================================
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
    '2xl': 1920,
  },

  // ============================================
  // SIZES - Component Dimensions
  // ============================================
  sizes: {
    icon: {
      xs: '1rem', // 16px
      sm: '1.25rem', // 20px
      md: '1.5rem', // 24px
      lg: '2rem', // 32px
      xl: '2.5rem', // 40px
    },

    button: {
      sm: '2rem', // 32px
      md: '2.5rem', // 40px
      lg: '3rem', // 48px
    },

    input: {
      sm: '2rem', // 32px
      md: '2.5rem', // 40px
      lg: '3rem', // 48px
    },
  },
} as const;

export type DesignTokens = typeof designTokens;
